/**
 * Profile.tsx — User profile & account management page.
 *
 * Features:
 *  - View and edit display name
 *  - Change password (with strength meter)
 *  - Active sessions list with "Revoke" per session + "Logout everywhere"
 */

import React, { useState, useEffect, useMemo } from 'react';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ComputerIcon from '@mui/icons-material/Computer';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import axios from 'axios';
import API_BASE from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import AccountIdBadge from '../../components/AccountIdBadge';
import { useShieldTheme } from '../../theme/ThemeContext';
import AppearancePanel from '../../components/AppearancePanel';

// ── Strength helpers (same as ResetPassword) ──────────────────────────────────

function evaluateStrength(pw: string) {
  const hasLength    = pw.length >= 8;
  const hasUppercase = /[A-Z]/.test(pw);
  const hasNumber    = /[0-9]/.test(pw);
  const hasSpecial   = /[^A-Za-z0-9]/.test(pw);
  let score = 0;
  if (hasLength) { score += 20; score += Math.min(36, (pw.length - 8) * 3); }
  if (hasUppercase) score += 15;
  if (hasNumber)    score += 15;
  if (hasSpecial)   score += 14;
  return { score, hasLength, hasUppercase, hasNumber, hasSpecial };
}
function strengthColor(s: number) {
  if (s < 30) return '#ef233c'; if (s < 55) return '#f77f00';
  if (s < 75) return '#f4d03f'; return '#2ecc71';
}

// ── Session row ───────────────────────────────────────────────────────────────

interface Session {
  id: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  expires_at: string;
  is_current?: boolean;
}

function SessionRow({ session, onRevoke }: { session: Session; onRevoke: (id: string) => void }) {
  const ua = session.user_agent || 'Unknown device';
  const short = ua.length > 60 ? ua.slice(0, 60) + '…' : ua;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        bgcolor: session.is_current ? 'rgba(0,180,216,0.08)' : 'transparent',
        border: session.is_current ? '1px solid rgba(0,180,216,0.2)' : '1px solid transparent',
      }}
    >
      <ComputerIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>{short}</Typography>
        <Typography variant="caption" color="text.secondary">
          {session.ip_address} · {new Date(session.created_at).toLocaleDateString()}
          {session.is_current && (
            <Chip label="This session" size="small" color="primary" sx={{ ml: 1, height: 16, fontSize: 10 }} />
          )}
        </Typography>
      </Box>
      {!session.is_current && (
        <IconButton size="small" onClick={() => onRevoke(session.id)} sx={{ color: 'error.main' }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function Profile() {
  const { user, logout } = useAuth();
  const { tokens: t } = useShieldTheme();

  // Profile edit
  const [editMode, setEditMode]   = useState(false);
  const [fullName, setFullName]   = useState(user?.full_name ?? '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  // Change password
  const [oldPwd, setOldPwd]       = useState('');
  const [newPwd, setNewPwd]       = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showOld, setShowOld]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdMsg, setPwdMsg]       = useState('');
  const [pwdError, setPwdError]   = useState('');

  // Sessions
  const [sessions, setSessions]   = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  const strength = useMemo(() => evaluateStrength(newPwd), [newPwd]);
  const pwdValid = strength.hasLength && strength.hasUppercase && strength.hasNumber && strength.hasSpecial;
  const pwdMatch = confirmPwd.length > 0 && newPwd === confirmPwd;

  useEffect(() => { fetchSessions(); }, []);

  async function fetchSessions() {
    setLoadingSessions(true);
    try {
      const { data } = await axios.get(`${API_BASE}/auth/sessions`);
      setSessions(data);
    } catch { /* ignore */ }
    setLoadingSessions(false);
  }

  async function saveProfile() {
    setSavingProfile(true);
    setProfileMsg('');
    try {
      await axios.patch(`${API_BASE}/auth/me`, { full_name: fullName.trim() });
      setProfileMsg('Profile updated.');
      setEditMode(false);
    } catch (err: any) {
      setProfileMsg(err.response?.data?.detail ?? 'Could not update profile.');
    } finally {
      setSavingProfile(false);
    }
  }

  async function changePassword() {
    setSavingPwd(true);
    setPwdError('');
    setPwdMsg('');
    try {
      await axios.post(`${API_BASE}/auth/change-password`, {
        old_password: oldPwd,
        new_password: newPwd,
      });
      setPwdMsg('Password changed. You have been logged out of all other sessions.');
      setOldPwd(''); setNewPwd(''); setConfirmPwd('');
      fetchSessions();
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setPwdError(typeof d === 'string' ? d : 'Could not change password.');
    } finally {
      setSavingPwd(false);
    }
  }

  async function revokeSession(id: string) {
    try {
      await axios.delete(`${API_BASE}/auth/sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch { /* ignore */ }
  }

  async function logoutEverywhere() {
    try {
      await axios.post(`${API_BASE}/auth/logout-all`);
      logout();
    } catch { logout(); }
  }

  const roleChipColor = user?.role === 'super_admin'
    ? 'error' : user?.role === 'engineer' ? 'warning' : 'default';

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', py: 4 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>My Profile</Typography>

      {/* Profile card */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 24 }}>
            {(user?.full_name ?? 'U')[0].toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>{user?.full_name}</Typography>
            <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
            <Chip
              label={user?.role?.replace('_', ' ')}
              size="small"
              color={roleChipColor as any}
              sx={{ mt: 0.5, textTransform: 'capitalize' }}
            />
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setEditMode((p) => !p)}>
            <EditIcon />
          </IconButton>
        </Box>

        {editMode && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            {profileMsg && <Alert severity="info" sx={{ mb: 2 }}>{profileMsg}</Alert>}
            <TextField
              label="Display name"
              fullWidth
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={saveProfile}
                disabled={savingProfile || !fullName.trim()}
              >
                {savingProfile ? <CircularProgress size={16} color="inherit" /> : 'Save'}
              </Button>
              <Button variant="text" size="small" onClick={() => { setEditMode(false); setFullName(user?.full_name ?? ''); }}>
                Cancel
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>

      {user?.role === 'customer' && user?.customer_id && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Account Information</Typography>
          <AccountIdBadge />
        </Paper>
      )}

      {/* Change password */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>Change Password</Typography>

        {pwdMsg   && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPwdMsg('')}>{pwdMsg}</Alert>}
        {pwdError && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setPwdError('')}>{pwdError}</Alert>}

        <TextField
          label="Current password"
          type={showOld ? 'text' : 'password'}
          fullWidth
          value={oldPwd}
          onChange={(e) => setOldPwd(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowOld((p) => !p)} edge="end" size="small">
                  {showOld ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          label="New password"
          type={showNew ? 'text' : 'password'}
          fullWidth
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNew((p) => !p)} edge="end" size="small">
                  {showNew ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        {newPwd.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={strength.score}
              sx={{
                height: 5,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': { bgcolor: strengthColor(strength.score), borderRadius: 3 },
              }}
            />
          </Box>
        )}

        <TextField
          label="Confirm new password"
          type="password"
          fullWidth
          value={confirmPwd}
          onChange={(e) => setConfirmPwd(e.target.value)}
          error={confirmPwd.length > 0 && !pwdMatch}
          helperText={confirmPwd.length > 0 && !pwdMatch ? "Passwords don't match" : ''}
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          onClick={changePassword}
          disabled={savingPwd || !oldPwd || !pwdValid || !pwdMatch}
        >
          {savingPwd ? <CircularProgress size={20} color="inherit" /> : 'Update Password'}
        </Button>
      </Paper>

      {/* ── Appearance ──────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{
            width: 3, height: 14,
            background: t.primary,
            borderRadius: t.radiusFull,
            flexShrink: 0,
          }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Appearance
          </Typography>
        </Box>
        <AppearancePanel showTitle={true} compact={false} adminMode={false} />
      </Box>

      {/* Active sessions */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>Active Sessions</Typography>
          <Button
            size="small"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={logoutEverywhere}
          >
            Logout everywhere
          </Button>
        </Box>

        {loadingSessions ? (
          <CircularProgress size={24} />
        ) : sessions.length === 0 ? (
          <Typography color="text.secondary" variant="body2">No active sessions found.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {sessions.map((s) => (
              <SessionRow key={s.id} session={s} onRevoke={revokeSession} />
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
